import { XmlEntities } from 'html-entities';
import * as xmlReader from 'xml-reader';
import * as xmlQuery from 'xml-query';
import { DOMParser, XMLSerializer } from 'xmldom';
import { fixNumberDecimals, isNullOrUndefined } from 'common/util/utils';
import { safelyRound } from 'common/util/number/numberUtil';
import { RubricType, TableSortOptions } from 'rubrics/src/components/rubricsTable/types';
import {
    BandedCriterion, BandedOrCriteriaMarksObject, Criterion, CriterionScoreDetail,
} from '../features/setMark/reducer';
import { CommentTypes } from '../features/commentsList/services/commentsService';
import { THRESHOLD_VALUE_STEP } from '../features/workflowSettings/services/thresholdService';
import { Threshold } from '../features/workflowSettings/reducer';

const xmlParser = {
    updateCompetenceLevelXML: (data: string, thresholdId: string, newValue: number) => {
        const xmlEntities = new XmlEntities();
        let foundIndex: number = null;
        const doc = getXmlDocFromString(xmlEntities.decode(data), 'text/xml');
        Array.from(doc.documentElement.getElementsByTagName('grade'))
            .forEach((gradeElement: HTMLElement, index: number) => {
                if (gradeElement.getAttribute('id') === thresholdId) {
                    foundIndex = index;
                    gradeElement.setAttribute('startValue', newValue.toString());
                }
                if (foundIndex !== null && (foundIndex + 1) === index) {
                    gradeElement.setAttribute('threshold', fixNumberDecimals((newValue - THRESHOLD_VALUE_STEP), 2));
                }
            });
        return `<?xml version="1.0" encoding="UTF-8"?>${new XMLSerializer().serializeToString(doc.documentElement)}`;
    },

    parseCompetenceLevel: (data: string): Threshold[] => {
        const doc = getXmlDocFromString(data, 'text/xml');
        return Array.from(doc.documentElement.getElementsByTagName('grade'))
            .map((grade: any) => {
                return {
                    id: grade.getAttribute('id'),
                    threshold: parseFloat(grade.getAttribute('threshold')),
                    startValue: parseFloat(grade.getAttribute('startValue')),
                    label: grade.getAttribute('label'),
                };
            });
    },

    parseBandedCriteriaMarks: (bandedCriteriaText: any, evaluators?: []): BandedCriterion[] => {
        const reader = xmlReader.create();
        const bandedCriteria: BandedCriterion[] = [];

        const yourMarks: any = evaluators ? evaluators.find((evalutalor: any) => {
            return evalutalor.you === true;
        }) : null;

        reader.on('done', (data: any) => {
            const xq = xmlQuery(data);
            const xmlEntities = new XmlEntities();
            const xqRubricBlocks = xq.find('rubricBlock');

            xqRubricBlocks.each((rubricBlock: any) => {
                const xqRubricBlock = xmlQuery(rubricBlock);
                const isRubricsMarking = xqRubricBlock.attr('use') === 'rubrics_marking';

                if (isNullOrUndefined(xqRubricBlock.attr('use')) || isRubricsMarking) {
                    const rubricOrder = xqRubricBlock.attr('bandDirection');
                    const rubricType = xqRubricBlock.attr('inspera:rubricsType');
                    const isPercentage = rubricType === RubricType.PERCENTAGE;
                    const minAttr = isPercentage ? 'data-reward-minimum-percentage' : 'data-reward-minimum-marks';
                    const maxAttr = isPercentage ? 'data-maximum-percentage' : 'data-maximum-marks';
                    xqRubricBlock.children().each((mark: any) => {
                        const xqMark = xmlQuery(mark);
                        if (xqMark.has('ul')) {
                            const criteriaId = xqMark.attr('data-aspect-id');
                            const criteriaMaxMark = Number(xqMark.attr(maxAttr));
                            const xqLevels = xqMark.find('ul').find('li');
                            const criterionScoreDetails: CriterionScoreDetail[] = [];

                            // Parse the score levels
                            xqLevels.each((level: any, index: number) => {
                                const xmlQueryLevel = xmlQuery(level);
                                const scoreLevelHeader = xmlQueryLevel.find('h3').text();
                                const scoreLevelDesc = xmlQueryLevel.find('div').text();
                                let prevLevelIndex = index - 1;
                                if (isRubricsMarking && rubricOrder === 'ltr') {
                                    prevLevelIndex = index + 1;
                                }
                                const prevLevel = index >= 0 ? xqLevels.get(prevLevelIndex) : null;

                                const minScore = safelyRound(Number(xmlQueryLevel.attr(minAttr)), 2);
                                const maxScore = prevLevel
                                    ? xmlQuery(prevLevel).attr(minAttr) === '0'
                                        ? 0 : safelyRound(Number(xmlQuery(prevLevel).attr(minAttr)) - 0.01, 2)
                                    : isPercentage ? 100 : criteriaMaxMark;
                                const criterionScoreDetail: CriterionScoreDetail = {
                                    title: xmlEntities.decode(scoreLevelHeader),
                                    description: xmlEntities.decode(scoreLevelDesc),
                                    minScore,
                                    maxScore,
                                };
                                criterionScoreDetails.push(criterionScoreDetail);
                            });


                            const criteriaMark = yourMarks && yourMarks.scoreDetail ? yourMarks.scoreDetail.find((criteria: any) => {
                                return criteria.criteriaId === criteriaId;
                            }) : null;
                            const rewardMark = criteriaMark && criteriaMark.rewardMarks >= 0 ? criteriaMark.rewardMarks : null;
                            const rewardPercentage = criteriaMark && criteriaMark.rewardPercentage >= 0 ? criteriaMark.rewardPercentage : null;
                            const criterionFeedback = criteriaMark && criteriaMark.criterionFeedback ? criteriaMark.criterionFeedback : null;
                            const criteriaTitle = xmlEntities.decode(xqMark.find('h2').text());
                            const criteria: BandedCriterion = {
                                criterionScoreDetails,
                                title: criteriaTitle,
                                id: criteriaId as string,
                                activeScore: isPercentage ? rewardPercentage : rewardMark,
                                maxMark: criteriaMaxMark,
                                rubricType: isNullOrUndefined(rubricType) ? RubricType.POINT_BASED : rubricType as RubricType,
                                criterionFeedback,
                                rubricScoreOrder: rubricOrder === 'ltr'
                                    ? TableSortOptions.ASCENDING_FL : TableSortOptions.DESCENDING_FL,
                            };

                            bandedCriteria.push(criteria);
                        }
                    });
                }
            });
        });

        reader.parse(bandedCriteriaText);

        return bandedCriteria;
    },

    getRubricOrder: (rubricXMl: any): string => {
        const reader = xmlReader.create();
        let result = 'rtl';
        reader.on('done', (data: any) => {
            const xq = xmlQuery(data);
            const xqRubricBlocks = xq.find('rubricBlock');
            if (xqRubricBlocks.length) {
                result = xqRubricBlocks.first().attr('bandDirection');
            }
        });

        reader.parse(rubricXMl);
        return result;
    },

    parseCriteriaMarks: (criteria: any, evaluators?: []): Criterion[] => {
        const reader = xmlReader.create();
        const xmlEntities = new XmlEntities();
        const yourMarks: any = evaluators ? evaluators.find((evalutalor: any) => {
            return evalutalor.you;
        }) : null;

        const criteriaCollection: Criterion[] = [];
        reader.on('done', (data: any) => {
            const xqData = xmlQuery(data);
            const xqRubricBlocks = xqData.find('rubricBlock');

            xqRubricBlocks.each((rubricBlock: any) => {
                const xqRubricBlock = xmlQuery(rubricBlock);

                if (xqRubricBlock.attr('use') === 'criteria_marking') {
                    const xqCriteria = xqRubricBlock.find('li');

                    xqCriteria.each((criterion: any) => {
                        const xqCriterion = xmlQuery(criterion);
                        const criterionText = xmlEntities.decode(xqCriterion.find('div').text());
                        const criterionId = xqCriterion.attr('data-criteria-id') as string;
                        const criterionScore = Number(xqCriterion.attr('data-reward-marks'));
                        const appliedOnCandidate = yourMarks?.scoreDetail?.some((criteria: any) => {
                            return criteria.criteriaId === criterionId;
                        }) || false;
                        criteriaCollection.push({
                            criterionId,
                            criterionText,
                            criterionScore,
                            appliedOnCandidate,
                        });
                    });
                }
            });
        });

        reader.parse(criteria);

        return criteriaCollection;
    },

    parseBandedOrCriteriaMarking: (xmlContent: any, evaluators?: []): BandedOrCriteriaMarksObject => {
        let res: BandedOrCriteriaMarksObject = null;
        const reader = xmlReader.create();

        if (isNullOrUndefined(xmlContent)) {
            return res;
        }

        reader.on('done', (data: any) => {
            const xq = xmlQuery(data);
            const rubricBlock = xq.find('rubricBlock');

            if (rubricBlock.children().length > 0) {
                const useAttributes: string[] = [];
                rubricBlock.each((rubricBlock: any) => {
                    const useAttribute: any = xmlQuery(rubricBlock).attr('use');

                    if (isNullOrUndefined(useAttribute)) {
                        useAttributes.push('banded_marking');
                    } else {
                        useAttributes.push(useAttribute);
                    }
                });
                /*
                 all should have view scorer, no matter of use attribute order:
                 a. no "use" attribute -> banded marking
                 b. use attribute "criteria_marking" -> criteria marking
                 c. use attribute "mark_scheme" - > mark scheme
               */

                // Criteria marking
                if (useAttributes.includes(CommentTypes.CRITERIA_MARKING)) {
                    res = {
                        type: CommentTypes.CRITERIA_MARKING,
                        marks: xmlParser.parseCriteriaMarks(xmlContent, evaluators),
                    };
                }

                // Banded  marking
                if (useAttributes.includes(CommentTypes.BANDED_MARKING)) {
                    res = {
                        type: CommentTypes.BANDED_MARKING,
                        marks: xmlParser.parseBandedCriteriaMarks(xmlContent, evaluators),
                    };
                }

                // Rubrics marking
                if (useAttributes.includes(CommentTypes.RUBRICS_MARKING)) {
                    res = {
                        type: CommentTypes.RUBRICS_MARKING,
                        marks: xmlParser.parseBandedCriteriaMarks(xmlContent, evaluators),
                    };
                }
            }
        });

        reader.parse(xmlContent);

        return res;
    },

    parseQuestionOrAlternativeFeedback: (feedback: any, feedbackType: string): any => {
        let feedbackContent = '';
        let alternativeIdentifier = '';
        const reader = xmlReader.create();

        reader.on('done', (data: any) => {
            const xq = xmlQuery(data);
            const feedback = feedbackType === CommentTypes.QUESTION_FEEDBACK
                ? xq.find('modalFeedback') : xq.find('feedbackInline');
            const xmlEntities = new XmlEntities();

            feedbackContent = xmlEntities.decode(feedback.text());
            alternativeIdentifier = feedback.attr('identifier');
        });

        reader.parse(feedback);

        return {
            feedbackContent,
            alternativeIdentifier,
        };
    },

    parseInteractionFeedback: (feedback: any, question: any): any => {
        let feedbackContent = '';
        const domParser = new DOMParser();
        const parsedDom = domParser.parseFromString(question.feedback[0], 'text/html');
        const printedVariables = parsedDom.getElementsByTagName('printedVariable');
        const { templateDeclarations, candidateResponses, interactionId } = question;
        Array.from(printedVariables).forEach((printedVar: any) => {
            const identifier = printedVar.getAttribute('identifier');
            let template;

            if (identifier) {
                if (identifier.includes(interactionId)) {
                    template = `${candidateResponses[0]}`;
                } else {
                    const templateDeclaration = templateDeclarations.find((item: any) => item.identifier === identifier);
                    const effectiveValues = templateDeclaration?.value || templateDeclaration?.defaultValue;
                    template = (Array.isArray(effectiveValues) && effectiveValues.join(','));
                }
            }
            printedVar.parentNode.replaceChild(domParser.parseFromString(`<span>${template}</span>`), printedVar);
        });
        const modalFeedbackElement = parsedDom.getElementsByTagName('modalFeedback').item(0);
        const identifier = modalFeedbackElement.getAttribute('identifier');
        feedbackContent = new XMLSerializer().serializeToString(modalFeedbackElement.firstChild);
        return {
            feedbackContent,
            identifier,
        };
    },

    parseMultipleChoiceInteraction: (itemBody: string): any => {
        let res: any[] = null;
        const reader = xmlReader.create();

        reader.on('done', (data: any) => {
            const xq = xmlQuery(data);
            const interactions = xq.find('choiceInteraction');
            interactions.each((interaction: any) => {
                let prompt: any;
                const choices: any[] = [];
                interaction.children.forEach((element: any) => {
                    if (isNullOrUndefined(res)) {
                        res = [];
                    }
                    if (element.name === 'prompt') {
                        if (element.children && element.children.length) {
                            prompt = element.children[0].value;
                        }
                    } else if (element.name === 'simpleChoice') {
                        let choiceText;
                        let hasMathLatex = false;
                        if (!isNullOrUndefined(element.children[0].value)) {
                            choiceText = element.children[0].value;
                        } else {
                            // fallback in case it has some weird structure,
                            // text is always in a node that has an empty name
                            // (even if it's in a span or something else)
                            const elementQuery = xmlQuery(element);
                            elementQuery.find('span').each((el: any) => {
                                if (el.attributes?.class?.includes('LaTeX')) {
                                    hasMathLatex = true;
                                }
                            });
                            choiceText = elementQuery.find('').text();
                        }
                        choices.push({
                            identifier: element.attributes.identifier,
                            text: choiceText,
                            hasMathLatex,
                        });
                    }
                });
                res.push({
                    prompt,
                    choices,
                    responseIdentifier: interaction.attributes.responseIdentifier,
                });
            });
        });

        reader.parse(itemBody);

        return res;
    },
};

function getXmlDocFromString(xmlString: any, mimeType: string) {
    return new DOMParser().parseFromString(xmlString, mimeType);
}

export default xmlParser;
